import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useField } from 'formik';
import { SVGS } from '../../../../../assets/images';
import styles from './form-number-input.module.scss';

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: React.ReactNode;
    name: string;
    placeholder?: string;
    style?: CSSProperties;
    step?: number;
    integerOnly?: boolean;
}

const DEFAULT_STEP = 0.5;

export const FormNumberInput = (props: Props) => {
    const {
        className,
        style,
        label,
        min,
        max,
        step = DEFAULT_STEP,
        integerOnly = false,
        required,
        ...inputProps
    } = props;
    const [field, meta, helpers] = useField<number | ''>({ ...inputProps, type: 'number' });
    const ref = useRef<HTMLInputElement>(null);
    const valueAsString = field.value === '' || field.value === undefined || field.value === null
        ? ''
        : String(field.value);
    const [draft, setDraft] = useState(valueAsString);

    useEffect(() => {
        setDraft(valueAsString);
    }, [valueAsString]);

    const normalize = (raw: string): number | null => {
        if (raw.trim() === '') {
            return null;
        }

        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) {
            return null;
        }

        const minValue = min === undefined ? undefined : Number(min);
        const maxValue = max === undefined ? undefined : Number(max);

        if (minValue !== undefined && parsed < minValue) {
            return minValue;
        }
        if (maxValue !== undefined && parsed > maxValue) {
            return maxValue;
        }

        return parsed;
    };

    const commit = () => {
        const normalized = normalize(draft);

        helpers.setTouched(true, true);

        if (normalized === null) {
            setDraft(valueAsString);
            return;
        }

        if (!Object.is(normalized, field.value)) {
            helpers.setValue(normalized, true);
        }

        setDraft(String(normalized));
    };

    const applyStep = (direction: 1 | -1) => {
        const minValue = min === undefined ? undefined : Number(min);
        const baseValue = normalize(draft) ?? (typeof field.value === 'number' ? field.value : null) ?? minValue ?? 0;
        const nextValue = normalize(String(baseValue + step * direction));

        if (nextValue === null) {
            return;
        }

        setDraft(String(nextValue));
        helpers.setTouched(true, false);
        helpers.setValue(nextValue, true);
        ref.current?.focus();
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit();
            ref.current?.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(valueAsString);
            ref.current?.blur();
        }
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const nextValue = e.target.value;
        if (integerOnly && !/^-?\d*$/.test(nextValue)) {
            return;
        }

        setDraft(nextValue);
    };

    const inputClassName = [styles.input, className].filter(Boolean).join(' ');
    const showError = Boolean(meta.touched && meta.error);

    return (
        <label htmlFor={inputProps.id || inputProps.name} className={styles.wrapper} style={style}>
            <span className={styles.fieldName}>{label}</span>
            {required ? <span className={styles.asterisk}>*</span> : null}
            <div className={styles.numberInputWrapper}>
                <input
                    {...field}
                    {...inputProps}
                    ref={ref}
                    type='number'
                    value={draft}
                    min={min}
                    max={max}
                    step={step}
                    className={inputClassName}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={commit}
                />
                <div className={styles.stepperButtons}>
                    <button
                        type='button'
                        className={styles.stepperButton}
                        aria-label='Increase value'
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyStep(1)}>
                        <SVGS.KeyboardArrowDownIcon className={styles.stepperIconUp} />
                    </button>
                    <button
                        type='button'
                        className={styles.stepperButton}
                        aria-label='Decrease value'
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyStep(-1)}>
                        <SVGS.KeyboardArrowDownIcon className={styles.stepperIconDown} />
                    </button>
                </div>
            </div>
            {showError ? <div className={styles.error}>{meta.error}</div> : null}
        </label>
    );
};

